import dayjs from 'dayjs';
import type { Spending, SpendingDetail } from '../../../api/financials-client/financials-client.type';
import type { Project } from '../../../api/projects-client/projects-client.type';

/**
 * Get the available spending years
 *
 * @param {Spending[] | undefined} spendingData - The spending data
 * @returns {Set<number>} A set of the years that have spending data, or can have spending data
 */
function getAvailableSpendingYears(spendingData: Spending[] | undefined): Set<number> {
  const spendingYears = new Set((spendingData || []).map((item) => item.year));

  // if we do not have spending data for the current year, add the current year to the set
  if (!spendingYears.has(dayjs().year())) {
    spendingYears.add(dayjs().year());
  }

  return spendingYears;
}

/**
 * Filters projects and their subprojects based on a search term
 *
 * @param {Project[]} projects - Array of projects to filter
 * @param {string} searchTerm - Search term to filter by
 * @returns {Project[]} Filtered array of projects with matching subprojects
 */
function filterProjectsBySearch(projects: Project[], searchTerm: string): Project[] {
  if (!searchTerm) return projects;

  const searchTermLower = searchTerm.toLowerCase();

  return projects
    .map((project) => {
      const projectMatches = project.name.toLowerCase().includes(searchTermLower);
      const matchingSubprojects = project.subprojects.filter((subproject) =>
        subproject.name.toLowerCase().includes(searchTermLower),
      );

      // If project matches or has matching subprojects, include it with filtered subprojects
      if (projectMatches || matchingSubprojects.length > 0) {
        return {
          ...project,
          subprojects: projectMatches ? project.subprojects : matchingSubprojects,
        };
      }
      return null;
    })
    .filter((project): project is Project => project !== null);
}

/**
 * Get the total spending for a given year
 *
 * @param {Spending[] | undefined} spendingData - The spending data
 * @param {number | null} selectedYear - The year to get the total spending for
 * @returns {number | null} The total spending for the given year
 */
function getTotalSpending(spendingData: Spending[] | undefined, selectedYear: number | null): number | null {
  if (!spendingData || spendingData.length === 0 || !selectedYear) {
    return null;
  }

  return spendingData
    .filter((entry: any) => entry.year === selectedYear && !!entry.subproject_id)
    .reduce((acc, entry) => acc + (entry.budget || 0), 0);
}

/**
 * Rollup subproject spending for a given year
 *
 * @param {Spending[] | undefined} spendingData - The spending data
 * @param {number | null} selectedYear - The year to rollup the spending for
 * @returns {Record<string, number[]> | null} The rolled up spending data as a record of project ids with a list of monthly spending amounts as values
 *
 * example output:
 * {
 *  "project_id_1": [100, 200, 300],
 *  "project_id_2": [400, 500, 600],
 * }
 */
function rollupSubprojectSpending(
  spendingData: Spending[] | undefined,
  selectedYear: number | null,
): Record<string, number[]> | null {
  if (!spendingData || spendingData.length === 0 || !selectedYear) {
    return null;
  }

  const distinctProjects = new Set(
    spendingData.filter((entry: any) => entry.year === selectedYear).map((entry: any) => entry.project_id),
  );

  const perProjectSpendingRollup: Record<string, number[]> = {};
  Array.from(distinctProjects).forEach((projectId) => {
    const projectSpending = spendingData.filter(
      (entry) => entry.year === selectedYear && entry.project_id === projectId && entry.budget !== null,
    );
    const monthsWithBudget = new Set(projectSpending.map((entry: any) => entry.month));

    const rollup = [] as number[];
    Array.from(monthsWithBudget).forEach((month) => {
      const monthSpending = projectSpending.filter((entry: any) => entry.month === month);
      rollup.push(monthSpending.reduce((acc, entry) => acc + (entry.budget || 0), 0));
    });

    perProjectSpendingRollup[projectId] = rollup;
  });

  return perProjectSpendingRollup;
}

/**
 * Get the average monthly spending per project for a given year, then average the results
 *
 * @param {Spending[] | undefined} spendingData - The spending data
 * @param {number | null} selectedYear - The year to get the average monthly spending for
 * @returns {number | null} The average monthly spending per project for the given year
 */
function getAverageMonthlySpendingPerProject(
  spendingData: Spending[] | undefined,
  selectedYear: number | null,
): number | null {
  const perProjectSpendingRollup = rollupSubprojectSpending(spendingData, selectedYear);

  if (!perProjectSpendingRollup) {
    return null;
  }

  const perProjectSpendingAverages = Object.fromEntries(
    Object.entries(perProjectSpendingRollup).map(([projectId, rollup]) => {
      return rollup.length
        ? [projectId, rollup.reduce((acc, entry) => acc + entry, 0) / rollup.length]
        : [projectId, 0];
    }),
  );

  return Object.keys(perProjectSpendingAverages).length
    ? Object.values(perProjectSpendingAverages).reduce((acc, entry) => acc + entry, 0) /
        Object.keys(perProjectSpendingAverages).length
    : null;
}

/**
 * Get the median monthly spending per project for a given year, then calculate the median of the results
 *
 * @param {Spending[] | undefined} spendingData - The spending data
 * @param {number | null} selectedYear - The year to get the median monthly spending for
 * @returns {number | null} The median monthly spending per project for the given year
 */
function getMedianMonthlySpendingPerProject(
  spendingData: Spending[] | undefined,
  selectedYear: number | null,
): number | null {
  const perProjectSpendingRollup = rollupSubprojectSpending(spendingData, selectedYear);

  if (!perProjectSpendingRollup || Object.keys(perProjectSpendingRollup).length === 0) {
    return null;
  }

  const perProjectSpendingMedians = Object.fromEntries(
    Object.entries(perProjectSpendingRollup).map(([projectId, rollup]) => {
      const sortedRollup = rollup.sort((a, b) => a - b);
      return [
        projectId,
        rollup.length % 2 === 0
          ? (sortedRollup[rollup.length / 2 - 1] + sortedRollup[rollup.length / 2]) / 2
          : sortedRollup[Math.floor(rollup.length / 2)],
      ];
    }),
  );

  const sortedMedians = Object.values(perProjectSpendingMedians).sort((a, b) => a - b);

  return sortedMedians.length % 2 === 0
    ? (sortedMedians[sortedMedians.length / 2 - 1] + sortedMedians[sortedMedians.length / 2]) / 2
    : sortedMedians[Math.floor(sortedMedians.length / 2)];
}

/**
 * Get the minimum monthly spend for a given year
 *
 * @param {Spending[] | undefined} spendingData - The spending data
 * @param {number | null} selectedYear - The year to get the minimum monthly spend for
 * @returns {number | null} The minimum monthly spend for the given year
 */
function getMinimumMonthlySpend(spendingData: Spending[] | undefined, selectedYear: number | null): number | null {
  const perProjectSpendingRollup = rollupSubprojectSpending(spendingData, selectedYear);

  if (!perProjectSpendingRollup) {
    return null;
  }

  const sortedMinValues = Object.values(perProjectSpendingRollup).map((rollup) => Math.min(...rollup));
  return sortedMinValues.length ? Math.min(...sortedMinValues) : null;
}

/**
 * Get the maximum monthly spend for a given year
 *
 * @param {Spending[] | undefined} spendingData - The spending data
 * @param {number | null} selectedYear - The year to get the maximum monthly spend for
 * @returns {number | null} The maximum monthly spend for the given year
 */
function getMaximumMonthlySpend(spendingData: Spending[] | undefined, selectedYear: number | null): number | null {
  const perProjectSpendingRollup = rollupSubprojectSpending(spendingData, selectedYear);

  if (!perProjectSpendingRollup) {
    return null;
  }

  const sortedMaxValues = Object.values(perProjectSpendingRollup).map((rollup) => Math.max(...rollup));
  return sortedMaxValues.length ? Math.max(...sortedMaxValues) : null;
}

/**
 * Transform the spending data into the required format
 *
 * @param {Spending[] | undefined} spendingData - The spending data
 * @param {number | null} selectedYear - The year to transform the spending data for
 * @param {string} portfolioId - The portfolio id, used as the canonical owner of the payload. Passed
 *   in by the caller rather than sniffed from `spendingDataToSave[0].portfolio_id` so
 *   first-time saves (no persisted data yet) still produce a valid UUID payload.
 * @returns {Record<string, Record<string, SpendingDetail[]>> | null} The transformed spending data
 */
function buildSpendingPayload(
  spendingData: Spending[] | undefined,
  selectedYear: number | null,
  portfolioId: string,
): { portfolio_id: string; year: number; projects: Record<string, Record<string, SpendingDetail[]>> } | null {
  if (!spendingData || spendingData.length === 0 || !selectedYear) {
    return null;
  }

  const spendingDataToSave = spendingData.filter((entry: any) => entry.year === selectedYear && entry.budget !== '');

  // Transform the data into the required format
  const payload = {
    portfolio_id: portfolioId,
    year: selectedYear,
    projects: spendingDataToSave.reduce(
      (acc, entry) => {
        const projectId = entry.project_id;
        const subprojectId = entry.subproject_id || 'self';

        if (!acc[projectId]) {
          acc[projectId] = {};
        }

        if (!acc[projectId][subprojectId]) {
          acc[projectId][subprojectId] = [];
        }

        acc[projectId][subprojectId].push({
          month: entry.month,
          budget: entry.budget ?? 0,
        });

        if (!acc[projectId]['self']) {
          acc[projectId]['self'] = [];
        }

        const selfBudgetForMonth = acc[projectId]['self'].find((item) => item.month === entry.month);

        if (selfBudgetForMonth && selfBudgetForMonth.budget) {
          selfBudgetForMonth.budget += entry.budget ?? 0;
        } else {
          acc[projectId]['self'].push({
            month: entry.month,
            budget: entry.budget ?? 0,
          });
        }
        return acc;
      },
      {} as Record<string, Record<string, SpendingDetail[]>>,
    ),
  };

  return payload;
}

/**
 * Get available year options for the By Sprint view
 *
 * @param {{ start_date: string }[] | undefined} workPeriods - Work periods from the selected board
 * @param {Set<number>} fallbackYears - Available years derived from spending data, used when no work periods exist
 * @returns {value: string; label: string}[] Sorted year options (most recent first) for the year dropdown
 * @returns Sorted year options (most recent first) for the year dropdown
 */
function getAvailableSprintYears(
  workPeriods: { start_date: string }[] | undefined,
  fallbackYears: Set<number>,
): { value: string; label: string }[] {
  const years = new Set<number>();

  (workPeriods || []).forEach((workPeriod) => {
    const year = dayjs(workPeriod.start_date).year();
    years.add(year);
  });

  // When no work periods produce years, fall back to spending-derived years
  if (years.size === 0) {
    for (const year of fallbackYears) {
      years.add(year);
    }
  }

  // Always include the current year as a minimum fallback
  if (years.size === 0) {
    years.add(dayjs().year());
  }

  const sortedYears = Array.from(years).sort((a, b) => b - a);
  return sortedYears.map((year) => ({
    value: year.toString(),
    label: year.toString(),
  }));
}

export {
  buildSpendingPayload,
  filterProjectsBySearch,
  getAvailableSpendingYears,
  getAvailableSprintYears,
  getAverageMonthlySpendingPerProject,
  getMaximumMonthlySpend,
  getMedianMonthlySpendingPerProject,
  getMinimumMonthlySpend,
  getTotalSpending,
};
